import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

interface CreateColumnData {
  name: string;
  description?: string;
  color?: string;
  position?: number;
}

interface CreateColumnResponse {
  id: string;
  name: string;
  description: string;
  color: string;
  position: number;
  isDefault: boolean;
  deleted: boolean;
}

async function createColumn(
  token: string | undefined,
  workspaceId: string,
  data: CreateColumnData,
): Promise<CreateColumnResponse> {
  const bearer = resolveBearerToken(token);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const response = await axios.post<CreateColumnResponse>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/columns`,
    data,
    { headers },
  );

  return response.data;
}

export function useCreateColumn() {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const queryClient = useQueryClient();

  // Helper function to extract workspace ID from workspace object
  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    // Try id first, then SK, then extract from SK if it starts with "WS#"
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      // If SK starts with "WS#", extract the UUID part
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3); // Remove "WS#" prefix
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);

  return useMutation({
    mutationFn: (data: CreateColumnData) => createColumn(user?.signInUserSession?.idToken?.jwtToken, workspaceId, data),
    onSuccess: async () => {
      console.log('✅ Column created successfully');
      console.log('🔄 Invalidating tickets query for workspace:', workspaceId);

      // Invalidate tickets query to get updated columns - React Query will automatically refetch since staleTime is 0
      await queryClient.invalidateQueries([queryKeys.tickets, workspaceId]);

      console.log('✅ Query invalidated, refetch will happen automatically');
    },
    onError: (error: any) => {
      console.error('Failed to create column:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        headers: error?.response?.headers,
      });
      // You can add toast notifications here if you have a toast system
    },
  });
}
